<template>
  <div class="welcome-container text-left white--text py-2" v-if="company">
    <!-- Company Profile Header -->
    <div class="d-flex align-center mb-3">
      <v-avatar
        color="rgba(255, 255, 255, 0.2)"
        size="56"
        class="mr-3 elevation-2"
      >
        <img
          :src="logo"
          :alt="`${company?.name}` || `Company Portal`"
          @error="imgError = true"
          v-if="logo"
        />
        <v-icon color="light-blue lighten-3" large v-else>mdi-domain</v-icon>
      </v-avatar>
      <div>
        <h6
        v-if="isMobile"
          class="text-h6 font-weight-black text-uppercase tracking-wide white--text mb-0"
        >
          {{ company.name || "Company Portal" }}
        </h6>
        <h5
        v-else
          class="text-h5 font-weight-black text-uppercase tracking-wide white--text mb-0"
        >
          {{ company.name || "Company Portal" }}
        </h5>
        <span
          class="caption text-uppercase font-weight-medium light-blue--text text--lighten-3"
        >
          Official Portal & Operational Gateway
        </span>
      </div>
    </div>

    <!-- Company Tagline / Description -->
    <p
    v-if="!isMobile"
      class="text-subtitle-2 font-weight-light grey--text text--lighten-3 mb-4 line-height-relaxed"
    >
      Welcome to our secure portal. Log in to access the administrative
      dashboard.
    </p>

    <!-- Company Contact & Location Card (Advertising Mini-Profile) -->
    <v-card flat class="glass-card pa-4 rounded-lg mb-4">
      <div
        class="body-2 font-weight-bold light-blue--text text--lighten-3 mb-2 text-uppercase tracking-wide"
      >
        <v-icon small color="light-blue lighten-3" class="mr-1"
          >mdi-office-building</v-icon
        >
        Company Contact Details
      </div>

      <v-row dense class="caption">
        <!-- Physical Address -->
        <v-col cols="12" v-if="company.primary_address">
          <div class="d-flex align-start mb-1">
            <v-icon small color="grey lighten-1" class="mr-2 mt-1"
              >mdi-map-marker-outline</v-icon
            >
            <div>
              <span class="white--text">{{ company.primary_address }}</span>
              <span v-if="company.city">, </span><br /><span
                >{{ company.city }}<span v-if="company.city">, </span>
                {{ company.postal_code }}</span
              >
            </div>
          </div>
        </v-col>

        <!-- Primary Phone -->
        <v-col cols="12" sm="6" v-if="company.primary_phone">
          <div class="d-flex align-center mb-1">
            <v-icon small color="grey lighten-1" class="mr-2"
              >mdi-phone-outline</v-icon
            >
            <a
              :href="`tel:${company.primary_phone}`"
              class="white--text text-decoration-none"
            >
              {{ company.primary_phone }}
            </a>
          </div>
        </v-col>

        <!-- Company Email -->
        <v-col cols="12" sm="6" v-if="company.email">
          <div class="d-flex align-center mb-1">
            <v-icon small color="grey lighten-1" class="mr-2"
              >mdi-email-outline</v-icon
            >
            <a
              :href="`mailto:${company.email}`"
              class="white--text text-decoration-none text-truncate"
            >
              {{ company.email }}
            </a>
          </div>
        </v-col>

        <v-col cols="12" sm="6">
          <v-btn
            block
            x-large
            elevation="0"
            class="glass-card custom-btn-card text-none pa-3 rounded-lg text-left"
            @click="gotoContactUs()"
          >
            <div class="d-flex align-center w-100">
              <v-icon color="yellow lighten-3" class="mr-2 flex-shrink-0"
                >mdi-comment</v-icon
              >
              <div class="text-truncate">
                <div class="body-2 font-weight-bold yellow--text text-wrap">
                  Contact Us
                </div>
                <div class="caption yellow--text text--lighten-4 text-wrap">
                  Chat with an admin
                  <v-icon right dark>mdi-arrow-right</v-icon>
                </div>
              </div>
            </div>
          </v-btn>
        </v-col>
      </v-row>
    </v-card>

    <!-- Tech Support & Creator Info -->
    <v-card flat class="glass-card pa-3 rounded-lg mb-4">
      <div class="d-flex align-center justify-space-between flex-wrap ga-2">
        <!-- Creator Branding -->
        <div class="d-flex align-center">
          <v-avatar size="32" class="mr-2 pa-1" color="white">
            <img
              :src="logoSrcDark"
              alt="Baron IT Solutions Logo"
              @error="imgError = true"
              v-if="!imgError"
            />
            <v-icon v-else color="primary" small>mdi-shield-check</v-icon>
          </v-avatar>
          <div>
            <div class="caption grey--text text--lighten-2">Powered by</div>
            <div class="caption font-weight-bold white--text">
              Baron IT Solutions
            </div>
          </div>
        </div>

        <v-divider
          vertical
          class="mx-2 hidden-xs-only"
          style="border-color: rgba(255, 255, 255, 0.2)"
        ></v-divider>

        <!-- Support Actions -->
        <div class="d-flex align-center flex-wrap ga-1">
          <v-btn
            small
            text
            dark
            class="text-none px-2"
            href="https://wa.me/27814826615"
            target="_blank"
          >
            <v-icon small color="green lighten-2" class="mr-1"
              >mdi-whatsapp</v-icon
            >
            081 482 6615
          </v-btn>

          <v-btn
            small
            text
            dark
            class="text-none px-2"
            href="mailto:mail@baronitsolutions.co.za"
          >
            <v-icon small color="light-blue lighten-3" class="mr-1"
              >mdi-email-outline</v-icon
            >
            Support
          </v-btn>
        </div>
      </div>
    </v-card>

    <!-- Mobile Scroll Trigger -->
    <div v-if="$vuetify.breakpoint.smAndDown" class="text-center pt-2">
      <v-btn
        outlined
        dark
        block
        large
        rounded
        class="white--text border-white"
        @click="$emit('scroll-to-login')"
      >
        Continue to Login
        <v-icon right>mdi-arrow-down</v-icon>
      </v-btn>
    </div>
  </div>
</template>

<script>
import companiesJs from "@/plugins/companies"
import helpersJs from "@/plugins/helpers"

export default {
  name: "CompanyWelcome",
  props:{
    company:{
      default: null
    }
  },
  computed:{
    getApiUrl(){
      return this.$store.getters.getApiUrl
    },
    isMobile(){
      return this.$store.getters.isMobile
    }
  },
  data() {
    return {
      imgError: false,
      logoSrc: require("@/assets/logos/baron-it-logo.png"),
      logoSrcDark: require("@/assets/logos/baron-it-logo-dark.jpeg"),
      logo:null,
    };
  },
  watch:{
    company:{
      immediate: true,
      handler(newVal){
        if(!newVal || !newVal.emis) return;
        this.getLogos(newVal.emis)
      }
    }
  },

  methods:{
      gotoContactUs(){
        localStorage.removeItem("selectedDirectoryCompany")
        const company = this.company?.company || this.company || null

        this.$router.push({ name: "contact-us", params:{slug: company.slug}})
    },
    async getLogos(emis){
      const images = await companiesJs.getLogos(emis);
      if(images && images.length > 0){
        this.logo = this.getApiUrl + "/images/logos/" + emis + "/" + images[0]
      }
    }
  }
};
</script>

<style scoped>
.welcome-container {
  max-width: 520px;
  margin: 0 auto;
}

.glass-card {
  background: rgba(255, 255, 255, 0.08) !important;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.15) !important;
}

.tracking-wide {
  letter-spacing: 1.5px !important;
}

.line-height-relaxed {
  line-height: 1.6;
}

.ga-1 {
  gap: 4px;
}
.ga-2 {
  gap: 8px;
}

.border-white {
  border-color: rgba(255, 255, 255, 0.5) !important;
}

/* Ensures the button behaves visually like a v-card container */
.custom-btn-card {
  height: 100% !important;
  min-height: 100% !important;
  display: flex !important;
  align-items: center !important;
  justify-content: flex-start !important;
  white-space: normal !important;
}

/* Fixes default Vuetify button inner content flex behavior */
.custom-btn-card ::v-deep .v-btn__content {
  width: 100%;
  justify-content: flex-start;
  white-space: normal;
  text-align: left;
}

.w-100 {
  width: 100%;
}
</style>
